// PermissionRoute Component - Permission-Based Access Guard
import React, { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, usePermissions } from '../../shared/hooks';
import { LoadingSpinner } from '../../shared/components';

/**
 * PermissionRoute component that requires specific permissions
 * Redirects to unauthorized page if user lacks required permissions
 *
 * @param {Object} props - Component props
 * @param {React.Component} props.children - Child components to render
 * @param {Array|string} props.requiredPermissions - Required permissions
 * @param {string} props.requireAll - Whether all permissions are required (default: true)
 * @param {string} props.redirectTo - Redirect path for unauthorized users
 * @param {boolean} props.skipAuthCheck - Skip authentication check (default: false)
 * @returns {React.Component} Protected route or redirect
 */
export const PermissionRoute = ({
  children,
  requiredPermissions = [],
  requireAll = true,
  redirectTo = '/unauthorized',
  skipAuthCheck = false,
}) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  // Show loading spinner while checking authentication and permissions
  if (authLoading) {
    return (
      <div className="route-loading-container">
        <LoadingSpinner size="large" message="Checking permissions..." />
      </div>
    );
  }

  // Check authentication first (unless skipped)
  if (!skipAuthCheck && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Handle empty permissions array (allow access)
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return (
      <Suspense
        fallback={
          <div className="route-loading-container">
            <LoadingSpinner size="medium" message="Loading..." />
          </div>
        }
      >
        {children}
      </Suspense>
    );
  }

  // Normalize permissions to array
  const permissions = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];

  // Check permissions based on requireAll flag
  let hasRequiredPermissions = false;

  if (permissions.length === 1) {
    // Single permission check
    hasRequiredPermissions = hasPermission(permissions[0]);
  } else if (requireAll) {
    // All permissions required
    hasRequiredPermissions = hasAllPermissions(permissions);
  } else {
    // Any permission required
    hasRequiredPermissions = hasAnyPermission(permissions);
  }

  // Redirect if permissions are insufficient
  if (!hasRequiredPermissions) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render protected content with Suspense boundary
  return (
    <Suspense
      fallback={
        <div className="route-loading-container">
          <LoadingSpinner size="medium" message="Loading..." />
        </div>
      }
    >
      {children}
    </Suspense>
  );
};

/**
 * Higher-order component for permission-based route protection
 *
 * @param {React.Component} Component - Component to protect
 * @param {Array|string} requiredPermissions - Required permissions
 * @param {Object} options - Additional options
 * @returns {React.Component} Protected component
 */
export const withPermissions = (Component, requiredPermissions, options = {}) => {
  return function PermissionProtectedComponent(props) {
    return (
      <PermissionRoute requiredPermissions={requiredPermissions} {...options}>
        <Component {...props} />
      </PermissionRoute>
    );
  };
};

/**
 * Hook for conditional rendering based on permissions
 *
 * @param {Array|string} requiredPermissions - Required permissions
 * @param {boolean} requireAll - Whether all permissions are required
 * @returns {boolean} Whether user has required permissions
 */
export const usePermissionGuard = (requiredPermissions, requireAll = true) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  const permissions = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];

  if (permissions.length === 1) {
    return hasPermission(permissions[0]);
  }

  return requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
};

export default PermissionRoute;
